export type IntervalType = "month" | "year";

export enum SignupMethod {
  LOCAL = "local",
  SSO = "sso",
  GOOGLE = "google",
  LINKEDIN = "linkedin",
}

export type ProfileType = {
  user_id: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  email: string;
  oauth2_sso_config_id: number | null;
  signup_method: SignupMethod;
  stripe_reference_id: null | string;
  pricing_plan_id: null | number;
  pricing_plan?: {
    contact_us: boolean;
    max_projects: number;
    max_seats: number;
    max_storage_gb: number;
    max_team_members: number;
    max_schedule_reruns: number;
    max_budget_reruns: number;
    per_project_price: number;
    per_user_price: number;
    plan_name: string;
    price_per_month: number;
    price_per_year: number;
    pricing_plan_id: number;
    max_assistant_messages_per_project: number;
    max_summary_revisions: number;
  } | null;
  is_active: boolean;
  verified: boolean;
  created_at: string;
  updated_at: string;
  profile_image_path: string;
  role?: "admin" | "super_admin" | "user";
  posthog_identity_hash?: string | null;
  subscription?: {
    user_subscription_id: number;
    pricing_plan_id: number;
    interval: IntervalType;
    start_date: string;
    end_date: string;
    seats_purchased: number;
    projects_purchased: number;
    schedule_reruns_left: number;
    budget_reruns_left: number;
    free_seat_credits: number;
    free_project_credits: number;
    free_trial_project_credits: number;
    status: string;
    stripe_plan_subscription_id: string;
    stripe_addon_subscription_id: string | null;
    created_at: string;
    updated_at: string;
  };
  // null when no plan change is scheduled
  upcoming_pricing_plan?: {
    pricing_plan_id: number;
    interval: IntervalType;
    next_period_start: string;
  } | null;
  mfa_enabled: boolean;
  marketing_opt_in?: boolean;
  one_time_purchases?: {
    total_licenses: number;
    available_licenses: number;
    latest_purchase_date: Date | string | null;
    has_available_trial: boolean;
    has_active_trial: boolean;
    trial_days_remaining: number;
    trial_expiry_date: Date | string | null;
  } | null;
  isOneTimePurchase?: boolean;
  /** Merged from GET /projects/quotas for default workspace. */
  unlicensed_quota?: {
    count: number;
    max: number;
  };
  workspaces?: Array<{
    workspace_id: string;
    name: string;
    role: string;
    created_at: string;
    updated_at: string;
    image_path?: string | null;
    /** Merged from GET /projects/quotas when available (per-workspace OTP + usage). */
    quota?: {
      numberOfProjects?: number;
      available?: number;
      one_time_purchases?: {
        total_licenses: number;
        available_licenses: number;
        used_licenses?: number;
        has_available_trial: boolean;
        has_active_trial: boolean;
        trial_days_remaining?: number;
        trial_expiry_date?: Date | string | null;
      };
    };
    projects?: Array<{
      project_id: string;
      slug: string;
      name: string;
      production_type: string;
      archived: boolean;
      status: string;
      created_at: string;
      updated_at: string;
      project_image_path?: string;
      trial_expiry_date?: string | null;
    }>;
  }>;
};

/**
 * Type for profile data stored in TanStack Query cache
 * Matches the structure returned by /users/me endpoint
 */
export type ProfileCacheData = {
  user: ProfileType;
  workspaces: Array<{
    workspace_id: string;
    name: string;
    role: string;
    created_at: string;
    updated_at: string;
    image_path?: string | null;
    contribute_budget_benchmarks?: boolean;
    quota?: {
      numberOfProjects?: number;
      available?: number;
      one_time_purchases?: {
        total_licenses: number;
        available_licenses: number;
        used_licenses?: number;
        has_available_trial: boolean;
        has_active_trial: boolean;
        trial_days_remaining?: number;
        trial_expiry_date?: Date | string | null;
      };
    };
    projects?: Array<{
      project_id: string;
      slug: string;
      name: string;
      production_type: string;
      archived: boolean;
      status: string;
      created_at: string;
      updated_at: string;
      project_image_path?: string;
      trial_expiry_date?: string | null;
    }>;
  }>;
};

export type ProfileInfoType = {
  first_name: string;
  last_name: string;
};

export type ProfileImageType = {
  profile_image_path: string;
};

export type PricingPlanType = {
  pricing_plan_id: null | number;
};

export type ChangeEmailType = {
  email?: string;
};

export type ChangePasswordType = {
  current_password: string;
  new_password: string;
};

export type ScheduleRerunsLeft = {
  schedule_reruns_left: number;
};

export type SettingSectionType = "profile" | "notifications" | "general" | "teammates" | "billing";

export type NotificationItem = {
  notification_id: number;
  project_id: string;
  type: string;
  content: string;
  created_at: string;
  metadata?: any;
  title?: string;
  is_read: boolean;
};

export type CreateAutomatedDaybreaks = {
  every_x_pages: string | null;
  every_x_hours: number | null;
  start_date: Date | string | null;
  skip_days: Array<number>;
};

export type CreateManualDaybreak = {
  index: number;
  date: Date | string;
};
